import React, { useEffect, useMemo } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Radio,
  RadioGroup,
  Stack,
  SimpleGrid,
  Checkbox,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import { useGetStateCity, useGetInsurers } from "../queryHooks";


export enum BillingEntity {
  INSURANCE_COMPANY = "INSURANCE_COMPANY",
  END_CUSTOMER = "END_CUSTOMER",
  CUSTOMER = "CUSTOMER",
}

// Internal form data structure (flat)
export interface BillingDetailsData {
  billingEntity: BillingEntity | "";
  gst?: string;
  gstSelect?: string; // For storing the select value (can be "OTHER" or actual GST)
  manualGstNumber?: string; // For manual GST entry when "Other" is selected
  name: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pinCode: string;
  country: string;
  isSupportingBillRequired?: boolean;
  billingCustomerId?: string;
  billingCustomerInsurerFamily?: string;
  insuranceClaimId?: string;
}

// Output structure for API (nested address)
export interface BillingDetailsOutput {
  billingEntity: BillingEntity;
  gst?: string;
  name: string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode: string;
    country: string;
  };
  isSupportingBillRequired?: boolean;
  billingCustomerId?: string;
  billingCustomerInsurerFamily?: string;
  insuranceClaimId?: string;
}

interface BillingDetailsProps {
  billingDetails: BillingDetailsData;
  onBillingDetailsChange: (details: BillingDetailsOutput) => void;
  selectedCustomer: any;
}


const BillingDetails: React.FC<BillingDetailsProps> = ({
  billingDetails,
  onBillingDetailsChange,
  selectedCustomer,
}) => {
  const { data: stateCityData, isLoading: isLoadingStateCity } = useGetStateCity();
  const { data: insurersData, isLoading: isLoadingInsurers } = useGetInsurers();

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<BillingDetailsData>({
    defaultValues: billingDetails,
    mode: "onChange",
  });


  const watchedBillingEntity = watch("billingEntity");
  const watchedState = watch("state");
  const watchedCity = watch("city");
  
  const isCustomerSelected = watchedBillingEntity === BillingEntity.CUSTOMER;
  const isEndCustomerSelected = watchedBillingEntity === BillingEntity.END_CUSTOMER;
  const isInsuranceCompanySelected = watchedBillingEntity === BillingEntity.INSURANCE_COMPANY;
  const shouldShowFields = isCustomerSelected || isEndCustomerSelected || isInsuranceCompanySelected;
  const shouldUseCustomerData = isCustomerSelected && selectedCustomer;

  // Get customer address (default or first) - memoized
  const customerAddress = useMemo(() => {
    if (!selectedCustomer?.addresses || !Array.isArray(selectedCustomer.addresses)) {
      return null;
    }
    const defaultAddress = selectedCustomer.addresses.find((addr: any) => addr.isDefault);
    return defaultAddress || selectedCustomer.addresses[0] || null;
  }, [selectedCustomer]);

  // Transform flat structure to nested structure for API
  const transformToNested = (data: BillingDetailsData): BillingDetailsOutput | null => {
    if (!data.billingEntity || !data.name || !data.addressLine1 || !data.city || !data.state || !data.pinCode || !data.country) {
      return null;
    }
    // If "OTHER" is selected, use manualGstNumber; otherwise use gst
    const finalGst = data.gstSelect === "OTHER" ? data.manualGstNumber : data.gst;
    return {
      billingEntity: data.billingEntity as BillingEntity,
      gst: finalGst,
      name: data.name,
      address: {
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2,
        city: data.city,
        state: data.state,
        pinCode: data.pinCode,
        country: data.country,
      },
      isSupportingBillRequired: data.isSupportingBillRequired,
      billingCustomerId: data.billingCustomerId,
      billingCustomerInsurerFamily: data.billingCustomerInsurerFamily,
      insuranceClaimId: data.insuranceClaimId,
    };
  };

  // Populate form with customer data ONLY when CUSTOMER is selected
  useEffect(() => {
    if (shouldUseCustomerData && customerAddress) {
      const customerData: BillingDetailsData = {
        billingEntity: BillingEntity.CUSTOMER,
        gst: selectedCustomer.gstin || "",
        name: selectedCustomer.name || "",
        addressLine1: customerAddress.addressLine1 || "",
        addressLine2: customerAddress.addressLine2 || "",
        city: customerAddress.city || "",
        state: customerAddress.state || "",
        pinCode: customerAddress.pinCode || "",
        country: "India",
      };
      reset(customerData);
      const nestedData = transformToNested(customerData);
      if (nestedData) {
        onBillingDetailsChange(nestedData);
      }
    } else if (watchedBillingEntity && watchedBillingEntity !== BillingEntity.CUSTOMER) {
      // Reset form when switching away from CUSTOMER
      const resetData: BillingDetailsData = {
        billingEntity: watchedBillingEntity,
        gst: "",
        name: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        pinCode: "",
        country: "India",
      };
      reset(resetData);
    }
  }, [watchedBillingEntity, shouldUseCustomerData, customerAddress, selectedCustomer, reset, onBillingDetailsChange]);

  // Watch all form values for syncing
  const watchedGst = watch("gst");
  const watchedName = watch("name");
  const watchedAddressLine1 = watch("addressLine1");
  const watchedAddressLine2 = watch("addressLine2");
  const watchedPinCode = watch("pinCode");
  const watchedCountry = watch("country");
  const watchedInsuranceClaimId = watch("insuranceClaimId");
  const watchedIsSupportingBillRequired = watch("isSupportingBillRequired");
  
  const watchedGstSelect = watch("gstSelect");
  const watchedManualGstNumber = watch("manualGstNumber");
  
  // Ensure country is always set to "India"
  useEffect(() => {
    if (watchedCountry !== "India") {
      setValue("country", "India", { shouldDirty: false });
    }
  }, [watchedCountry, setValue]);
  
  // Check if insurance fields should be disabled (when GST is selected, but not "Other")
  const shouldDisableInsuranceFields = isInsuranceCompanySelected && !!watchedGstSelect && watchedGstSelect !== "OTHER";
  const isOtherSelected = isInsuranceCompanySelected && watchedGstSelect === "OTHER";

  // Sync form changes to parent - optimized with debounce
  useEffect(() => {
    if (shouldUseCustomerData) return;
    
    // When shouldDisableInsuranceFields is true, only sync insuranceClaimId and isSupportingBillRequired
    if (shouldDisableInsuranceFields) {
      const timeoutId = setTimeout(() => {
        // Get current billingDetails to preserve other fields
        const currentFormData: BillingDetailsData = {
          billingEntity: watchedBillingEntity,
          gstSelect: watchedGstSelect,
          gst: watchedGst,
          manualGstNumber: watchedManualGstNumber,
          name: watchedName,
          addressLine1: watchedAddressLine1,
          addressLine2: watchedAddressLine2,
          city: watchedCity,
          state: watchedState,
          pinCode: watchedPinCode,
          country: watchedCountry,
          insuranceClaimId: watchedInsuranceClaimId,
          isSupportingBillRequired: watchedIsSupportingBillRequired,
        };
        const nestedData = transformToNested(currentFormData);
        if (nestedData) {
          onBillingDetailsChange(nestedData);
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }

    const timeoutId = setTimeout(() => {
      const formData: BillingDetailsData = {
        billingEntity: watchedBillingEntity,
        gstSelect: watchedGstSelect,
        gst: watchedGst,
        manualGstNumber: watchedManualGstNumber,
        name: watchedName,
        addressLine1: watchedAddressLine1,
        addressLine2: watchedAddressLine2,
        city: watchedCity,
        state: watchedState,
        pinCode: watchedPinCode,
        country: watchedCountry,
        insuranceClaimId: watchedInsuranceClaimId,
        isSupportingBillRequired: watchedIsSupportingBillRequired,
      };
      const nestedData = transformToNested(formData);
      if (nestedData) {
        onBillingDetailsChange(nestedData);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    watchedBillingEntity,
    watchedGstSelect,
    watchedGst,
    watchedManualGstNumber,
    watchedName,
    watchedAddressLine1,
    watchedAddressLine2,
    watchedCity,
    watchedState,
    watchedPinCode,
    watchedCountry,
    watchedInsuranceClaimId,
    watchedIsSupportingBillRequired,
    shouldUseCustomerData,
    shouldDisableInsuranceFields,
    onBillingDetailsChange,
  ]);

  

  // Extract states and cities from API data - memoized
  const states = useMemo(() => {
    if (!stateCityData || typeof stateCityData !== "object" || Array.isArray(stateCityData)) {
      return [];
    }
    return Object.keys(stateCityData).sort();
  }, [stateCityData]);

  const cities = useMemo(() => {
    if (!stateCityData || typeof stateCityData !== "object" || Array.isArray(stateCityData) || !watchedState) {
      return [];
    }
    const stateCities = stateCityData[watchedState];
    return Array.isArray(stateCities) ? [...stateCities].sort() : [];
  }, [stateCityData, watchedState]);

  const stateOptions = useMemo(
    () => states?.map((state) => ({ value: state, label: state })),
    [states]
  );

  const cityOptions = useMemo(
    () => cities?.map((city) => ({ value: city, label: city })),
    [cities]
  );

  // Extract GST options from insurers API - memoized
  const gstOptions = useMemo(() => {
    if (!insurersData || !Array.isArray(insurersData)) {
      return [];
    }
    const options = insurersData
      .map((insurer: any) => {
        const gst = insurer.gstin || insurer.gst || insurer.gstNumber;
        return gst ? { value: gst, label: gst+"-"+insurer.name+ ( "-" + insurer.addresses?.[0]?.state || "") } : null;
      })
      .filter((option): option is { value: string; label: string } => option !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
    
    // Add "Other" option at the end
    return options;
  }, [insurersData]);

  // Helper function to find insurer by GST
  const findInsurerByGst = (gst: string) => {
    if (!insurersData || !Array.isArray(insurersData) || !gst) {
      return null;
    }
    return insurersData.find((insurer: any) => {
      const insurerGst = insurer.gstin || insurer.gst || insurer.gstNumber;
      return insurerGst === gst;
    }) || null;
  };

  // Handle "Other" option selection - clear fields and allow manual editing
  const handleOtherGstSelection = () => {
    setValue("name", "");
    setValue("addressLine1", "");
    setValue("addressLine2", "");
    setValue("city", "");
    setValue("state", "");
    setValue("pinCode", "");
    setValue("country", "");
    setValue("billingCustomerInsurerFamily", "");
    setValue("manualGstNumber", "");
    setValue("gst", "");
  };

  // Handle GST selection - auto-populate fields from insurer data
  const handleGstSelection = (selectedGst: string) => {
    const insurer = findInsurerByGst(selectedGst);
    if (!insurer) {
      return;
    }

    // Get default or first address
    const address = insurer.addresses?.find((addr: any) => addr.isDefault) || 
                   insurer.addresses?.[0] || null;
    
    // Prepare the data object
    const populatedData: BillingDetailsData = {
      billingEntity: watchedBillingEntity as BillingEntity,
      gstSelect: selectedGst,
      gst: selectedGst,
      name: insurer.name || "",
      addressLine1: address?.addressLine1 || "",
      addressLine2: address?.addressLine2 || "",
      city: address?.city || "",
      state: address?.state || "",
      pinCode: address?.pinCode || "",
      country: "India",
      billingCustomerInsurerFamily: insurer.insurerFamily || "",
      insuranceClaimId: watchedInsuranceClaimId || "",
      isSupportingBillRequired: watchedIsSupportingBillRequired || false,
    };
    
    // Set all values at once
    Object.keys(populatedData).forEach((key) => {
      setValue(key as keyof BillingDetailsData, populatedData[key as keyof BillingDetailsData] as any, { shouldDirty: true });
    });
    
    // Immediately sync to parent
    const nestedData = transformToNested(populatedData);
    if (nestedData) {
      onBillingDetailsChange(nestedData);
    }
  };

  // Handle GST clearance - reset all fields
  const handleGstClearance = () => {
    const clearedData: BillingDetailsData = {
      billingEntity: watchedBillingEntity as BillingEntity,
      gstSelect: "",
      gst: "",
      manualGstNumber: "",
      name: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pinCode: "",
      country: "India",
      billingCustomerInsurerFamily: "",
      insuranceClaimId: watchedInsuranceClaimId || "",
      isSupportingBillRequired: watchedIsSupportingBillRequired || false,
    };
    
    Object.keys(clearedData).forEach((key) => {
      setValue(key as keyof BillingDetailsData, clearedData[key as keyof BillingDetailsData] as any, { shouldDirty: true });
    });
  };

  // Main handler for GST select onChange
  const handleGstSelectChange = (selectedOption: any) => {
    const selectedGst = selectedOption ? selectedOption.value : "";
    
    if (selectedGst === "OTHER") {
      handleOtherGstSelection();
    } else if (selectedGst) {
      handleGstSelection(selectedGst);
    } else {
      handleGstClearance();
    }
  };

  // Form field configurations - memoized
  const formFields = useMemo(() => {
    if (isInsuranceCompanySelected) {
      return [
        { name: "name", label: "Name", placeholder: "Enter name", type: "input" },
        { name: "addressLine1", label: "Address Line 1", placeholder: "Enter address line 1", type: "input" },
        { name: "addressLine2", label: "Address Line 2", placeholder: "Enter address line 2 (optional)", type: "input", optional: true },
      ];
    }
    return [
      { name: "gst", label: "GST", placeholder: "Enter GST number", type: "input" },
      { name: "name", label: "Name", placeholder: "Enter name", type: "input" },
      { name: "addressLine1", label: "Address Line 1", placeholder: "Enter address line 1", type: "input" },
      { name: "addressLine2", label: "Address Line 2", placeholder: "Enter address line 2 (optional)", type: "input", optional: true },
    ];
  }, [isInsuranceCompanySelected]);

  const gridFields = useMemo(
    () => [
      { name: "pinCode", label: "Pin Code", placeholder: "Enter pin code", type: "input" },
      { name: "country", label: "Country", placeholder: "Enter country", type: "input" },
    ],
    []
  );

  const formFieldProps = useMemo(
    () => ({
      fontSize: { base: "xs", md: "smaller" },
      fontWeight: "medium" as const,
    }),
    []
  );

  const inputSize = useMemo(() => ({ base: "sm", md: "md" }), []);

  const renderInputField = (field: any) => {
    const isDisabled = shouldUseCustomerData || (shouldDisableInsuranceFields && field.name !== "insuranceClaimId") || watchedBillingEntity === BillingEntity.CUSTOMER;
    const isCountryField = field.name === "country";
    
    return (
      <FormControl key={field.name} isInvalid={!!errors[field.name as keyof typeof errors]}>
        <FormLabel {...formFieldProps}>{field.label}</FormLabel>
        <Controller
          name={field.name as keyof BillingDetailsData}
          control={control}
          render={({ field: controllerField }) => (
            <Input
              {...controllerField}
              value={isCountryField ? "India" : ((controllerField.value as string) || "")}
              placeholder={field.placeholder}
              bgColor="white"
              size={inputSize}
              readOnly={isDisabled || isCountryField}
            />
          )}
        />
      </FormControl>
    );
  };

  return (
    <Box
      width="100%"
    >
      <Box mb={5} borderBottom="1px solid lightgrey" pb={2}>
        <Text fontWeight="bold">Billing Details</Text>
      </Box>
      <VStack spacing={{ base: 3, md: 4 }} align="stretch">
        <FormControl>
          <FormLabel {...formFieldProps}>Billing Entity</FormLabel>
          <Controller
            name="billingEntity"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                }}
              >
                <Stack direction={{ base: "column", md: "row" }} spacing={{ base: 2, md: 5 }}>
                  <Radio value={BillingEntity.CUSTOMER} size={inputSize}>
                    Customer
                  </Radio>
                     <Radio value={BillingEntity.INSURANCE_COMPANY} size={inputSize}>
                    Insurance Company
                  </Radio>
                  <Radio value={BillingEntity.END_CUSTOMER} size={inputSize}>
                    End Customer
                  </Radio>
               
                </Stack>
              </RadioGroup>
            )}
          />
        </FormControl>

        {shouldShowFields && (
          <VStack spacing={{ base: 3, md: 4 }} align="stretch">
            {/* GST Field - Select for INSURANCE_COMPANY, Input for others */}
            {isInsuranceCompanySelected ? (
              <>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                  <FormControl isInvalid={!!errors.gstSelect}>
                    <FormLabel {...formFieldProps}>GST</FormLabel>
                    <Controller
                      name="gstSelect"
                      control={control}
                      render={({ field }) => {
                        const selectedOption = field.value
                          ? gstOptions.find((option) => option?.value === field.value) ||
                            (field.value ? { value: field.value, label: field.value } : null)
                          : null;
                        
                        return (
                          <Select
                            options={gstOptions}
                            value={selectedOption}
                            onChange={(selectedOption: any) => {
                              const selectedGst = selectedOption ? selectedOption.value : "";
                              field.onChange(selectedGst);
                              handleGstSelectChange(selectedOption);
                            }}
                            placeholder={isLoadingInsurers ? "Loading..." : "Select GST"}
                            isClearable
                            isSearchable
                            isLoading={isLoadingInsurers}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        );
                      }}
                    />
                  </FormControl>

                  <FormControl isInvalid={!!errors.insuranceClaimId}>
                    <FormLabel {...formFieldProps}>Insurance Claim ID</FormLabel>
                    <Controller
                      name="insuranceClaimId"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          value={(field.value as string) || ""}
                          placeholder="Enter insurance claim ID"
                          bgColor="white"
                          size={inputSize}
                          isDisabled={false}
                        />
                      )}
                    />
                  </FormControl>
                </SimpleGrid>

                {/* GST Number Input - shown when "Other" is selected */}
                {isOtherSelected && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                    <FormControl isInvalid={!!errors.manualGstNumber}>
                      <FormLabel {...formFieldProps}>GST Number</FormLabel>
                      <Controller
                        name="manualGstNumber"
                        control={control}
                        render={({ field }) => (
                          <Input
                            {...field}
                            value={(field.value as string) || ""}
                            placeholder="Enter GST number"
                            bgColor="white"
                            size={inputSize}
                          />
                        )}
                      />
                    </FormControl>
                    <Box /> {/* Empty box to maintain 2-column layout */}
                  </SimpleGrid>
                )}
              </>
            ) : null}

            {/* Render formFields in pairs */}
            {formFields.length > 0 && (
              <>
                {formFields?.map((field, index) => {
                  if (index % 2 === 0) {
                    const nextField = formFields[index + 1];
                    return (
                      <SimpleGrid key={index} columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                        {renderInputField(field)}
                        {nextField ? renderInputField(nextField) : <Box />}
                      </SimpleGrid>
                    );
                  }
                  return null;
                })}
              </>
            )}

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
              <FormControl isInvalid={!!errors.state}>
                <FormLabel {...formFieldProps}>State</FormLabel>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={stateOptions}
                      value={field.value ? stateOptions.find((option) => option.value === field.value) : null}
                      onChange={(selectedOption: any) => {
                        const newState = selectedOption ? selectedOption.value : "";
                        field.onChange(newState);
                        if (newState !== watchedState) {
                          setValue("city", "", { shouldDirty: false });
                        }
                      }}
                      placeholder={isLoadingStateCity ? "Loading..." : "Select state"}
                      isClearable
                      isSearchable
                      isDisabled={shouldUseCustomerData || shouldDisableInsuranceFields || isLoadingStateCity || watchedBillingEntity === BillingEntity.CUSTOMER}
                      isLoading={isLoadingStateCity}
                    />
                  )}
                />
              </FormControl>

              <FormControl isInvalid={!!errors.city}>
                <FormLabel {...formFieldProps}>City</FormLabel>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => {
                    const selectedOption = field.value
                      ? cityOptions.find((option) => option.value === field.value) || 
                        (field.value ? { value: field.value, label: field.value } : null)
                      : null;
                    
                    // Include current value in options if not present
                    const allCityOptions = field.value && !cityOptions.find(opt => opt.value === field.value)
                      ? [{ value: field.value, label: field.value }, ...cityOptions]
                      : cityOptions;
                    
                    return (
                      <Select
                        options={allCityOptions}
                        value={selectedOption}
                        onChange={(selectedOption: any) => {
                          field.onChange(selectedOption ? selectedOption.value : "");
                        }}
                        placeholder={watchedState ? "Select city" : "Select state first"}
                        isClearable
                        isSearchable
                        isDisabled={shouldUseCustomerData || shouldDisableInsuranceFields || !watchedState || watchedBillingEntity === BillingEntity.CUSTOMER}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    );
                  }}
                />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
              {gridFields?.map(renderInputField)}
            </SimpleGrid>

            {/* isSupportingBillRequired checkbox - only for INSURANCE_COMPANY */}
            {isInsuranceCompanySelected && (
              <FormControl>
                <Controller
                  name="isSupportingBillRequired"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      isChecked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    >
                      Supporting Bill Required
                    </Checkbox>
                  )}
                />
              </FormControl>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default BillingDetails;

